import { get } from "lodash";
import {
  COMBINED_FIELD_TYPE_STRING,
  DATA_RADIO_FIELD_TYPE_STRING,
  FIELD_TYPE_NUMBER_TO_STRING,
} from "features/configuration/common/constants";
import {
  getFilledCondition,
  getItemKey,
  getItemValue,
  itemShouldDisplay,
} from "@shared/features/configuration/utils";

export const getName = (item) => getItemKey(item, "name");
export const getIsEditable = (item) => getItemKey(item, "editable");
export const getChildren = (item) => get(item, ["data"], []);
export const getCreateItem = (item) => getItemKey(item, "create");
export const getItemOptions = (item, idToItem) =>
  getItemKey(item, "options", idToItem);
export const itemShouldOpenModal = (item) => getItemKey(item, "open_page");
export const getItemValueType = (item, idToItem) => {
  const itemValue = getItemValue(item, idToItem) || {};
  return itemValue.type;
};
export const getItemPrefix = (item, idToItem) =>
  getItemKey(item, "prefix", idToItem);
export const getItemDefaultDisplay = (item) =>
  getItemKey(item, "default_display");
export const getItemValidations = (item) => getItemKey(item, "validations");

export const getFieldType = (item, idToItem) => {
  const fieldTypeNumber = getItemKey(item, "field_type", idToItem);
  return FIELD_TYPE_NUMBER_TO_STRING[fieldTypeNumber];
};

export const setItemOverride = (item, checked) => {
  const filledCondition = getFilledCondition(item, "override");
  if (filledCondition) {
    filledCondition.outcome = checked;
  }
};

export const getItemOptionDisplay = (item, optionValue, idToItem) => {
  const options = getItemOptions(item, idToItem);
  const option = (options || []).find((option) => option.value === optionValue);
  return option ? option.name : null;
};

export const getItemMaxOptionValue = (item, idToItem) => {
  const options = getItemOptions(item, idToItem);
  return options
    ? Math.max(...options.map((option) => option.value))
    : undefined;
};

export const checkStrategyItemSelected = (strategyItem, itemValue) => {
  const childType = getItemValueType(strategyItem);
  return itemValue && childType
    ? itemValue.toString() === childType.toString()
    : strategyItem.chosen;
};

export const findChildElementWithMatchingValueType = (item, itemValueType) => {
  const children = getChildren(item);
  for (let childItem of children) {
    if (checkStrategyItemSelected(childItem, itemValueType)) {
      return childItem;
    }
  }
  for (let childItem of children) {
    const value = findChildElementWithMatchingValueType(
      childItem,
      itemValueType
    );
    if (value) {
      return value;
    }
  }
};

export const getItemDisplayString = (
  item,
  idToItem,
  inCombinedItem,
  inModal
) => {
  const children = getChildren(item);
  const fieldType = getFieldType(item, idToItem);
  if (fieldType === COMBINED_FIELD_TYPE_STRING) {
    const keyDisplay = getItemDisplayString(
      children[0],
      idToItem,
      true,
      inModal
    );
    const valueDisplay = getItemDisplayString(
      children[1],
      idToItem,
      true,
      inModal
    );
    const itemDisplay =
      (keyDisplay || "") +
      (valueDisplay ? (keyDisplay ? ": " : "") + valueDisplay : "");
    if (inModal && itemDisplay) {
      const itemName = getName(item);
      return itemName + ": " + itemDisplay;
    }
    return itemDisplay;
  } else if (fieldType === DATA_RADIO_FIELD_TYPE_STRING) {
    const itemValue = getItemValue(item, idToItem);
    const child = findChildElementWithMatchingValueType(item, itemValue);
    return getName(child);
  }
  if ("options" in item) {
    const shouldDisplay = itemShouldDisplay(item, idToItem);
    if (!shouldDisplay) return;
    const optionValue = getItemValue(item, idToItem);
    const name = getName(item);
    const optionDisplay =
      getItemOptionDisplay(item, optionValue, idToItem) || optionValue;
    const prefix = getItemPrefix(item, idToItem);
    if (!inCombinedItem && name && optionDisplay)
      return name + ": " + optionDisplay;
    if (optionDisplay)
      return (optionValue && prefix ? prefix : "") + optionDisplay;
    return;
  }
  return children
    .map((childItem) =>
      getItemDisplayString(childItem, idToItem, inCombinedItem, inModal)
    )
    .filter((display) => !!display)
    .join(", ");
};

export const getItemByName = (name, item) => {
  if (!item) return;
  const itemName = getName(item);
  if (itemName && itemName.toLowerCase() === name.toLowerCase()) {
    return item;
  }
  const children = getChildren(item);
  for (let child of children) {
    const item = getItemByName(name, child);
    if (item !== undefined) return item;
  }
};

export const getOptionText = (
  value,
  prefix,
  itemOption,
  suffix,
  delimiter = ""
) =>
  (value && prefix ? prefix + delimiter : "") +
  (itemOption.name || value) +
  (value && suffix ? delimiter + suffix : "");
